class PageNav extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  #extracted = false;
  /** @type {Map<string, Promise<HTMLElement>>} */
  #fetchCache = new Map();
  /** @type {string | null} */
  #initialHandle = null;
  /** @type {string | null} */
  #pendingHandle = null;

  connectedCallback() {
    if (!this.#extracted) {
      this.#extracted = true;
      const main = document.querySelector('#MainContent');
      if (main && main.contains(this)) {
        main.before(this);
        return;
      }
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#initialHandle = this.dataset.currentHandle || null;
    this.#tagOriginalMain();

    this.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener('click', this.#handleDocumentClick, { signal });
    window.addEventListener('popstate', this.#handlePopState, { signal });
  }

  disconnectedCallback() {
    if (this.#controller) {
      this.#controller.abort();
      this.#controller = null;
    }
  }

  #tagOriginalMain() {
    const main = document.querySelector('#MainContent');
    if (!main || !this.#initialHandle) return;
    if (main.hasAttribute('data-main-page-nav-content')) return;
    main.setAttribute('data-main-page-nav-content', '');
    main.setAttribute('data-page-handle', this.#initialHandle);
  }

  /** @returns {HTMLButtonElement | null} */
  get #trigger() {
    return this.querySelector('[data-page-nav-trigger]');
  }

  /** @returns {NodeListOf<HTMLButtonElement>} */
  get #items() {
    return this.querySelectorAll('[data-handle]');
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-page-nav-trigger]')) {
      this.#toggleOpen();
      return;
    }

    const item = target.closest('[data-handle]');
    if (item instanceof HTMLElement) {
      this.#selectItem(item);
    }
  };

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    this.#close();
  };

  #handlePopState = () => {
    const handle = this.#handleFromPath();
    if (!handle) {
      window.location.reload();
      return;
    }
    const item = this.querySelector(`[data-handle="${handle}"]`);
    if (!(item instanceof HTMLElement)) {
      window.location.reload();
      return;
    }
    this.#updateActiveState(item);
    this.#updateTriggerLabel(item);
    this.#swapContent(handle, `/pages/${handle}`);
  };

  #handleFromPath() {
    const match = window.location.pathname.match(/^\/pages\/([^/]+)\/?$/);
    return match ? match[1] : null;
  }

  #toggleOpen() {
    const trigger = this.#trigger;
    if (!trigger) return;
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
  }

  #close() {
    const trigger = this.#trigger;
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', 'false');
  }

  /** @param {HTMLElement} item */
  async #selectItem(item) {
    const handle = item.dataset.handle;
    if (!handle) return;

    this.#updateActiveState(item);
    this.#updateTriggerLabel(item);
    this.#close();

    const url = `/pages/${handle}`;
    if (window.location.pathname !== url) {
      window.history.pushState({ pageNavHandle: handle }, '', url);
    }

    await this.#swapContent(handle, url);
  }

  /** @param {HTMLElement} item */
  #updateActiveState(item) {
    this.#items.forEach((btn) => {
      btn.classList.remove('page-nav__item--active');
      btn.removeAttribute('aria-current');
    });
    item.classList.add('page-nav__item--active');
    item.setAttribute('aria-current', 'page');
  }

  /** @param {HTMLElement} item */
  #updateTriggerLabel(item) {
    const trigger = this.#trigger;
    if (!trigger) return;
    const label = trigger.querySelector('.page-nav__trigger-label');
    const text = item.textContent ? item.textContent.trim() : '';
    if (label && text) label.textContent = text;
  }

  /**
   * @param {string} handle
   * @param {string} url
   */
  async #swapContent(handle, url) {
    this.#pendingHandle = handle;

    let target = this.#findMain(handle);

    if (target && !target.hidden) return;

    window.scrollTo({ top: 0, behavior: 'instant' });

    if (!target) {
      this.#ensureSkeleton();
      try {
        target = await this.#fetchAndCreate(handle);
      } catch (error) {
        console.error('[page-nav] fetch failed', error);
        if (this.#pendingHandle === handle) {
          this.#removeSkeleton();
          window.location.href = url;
        }
        return;
      }
    }

    if (this.#pendingHandle !== handle) return;

    this.#removeSkeleton();

    if (!target) {
      window.location.href = url;
      return;
    }

    this.#showMain(target);
  }

  /** @param {string} handle */
  #findMain(handle) {
    return /** @type {HTMLElement | null} */ (
      document.querySelector(`[data-main-page-nav-content][data-page-handle="${handle}"]`)
    );
  }

  /** @param {HTMLElement} target */
  #showMain(target) {
    document.querySelectorAll('[data-main-page-nav-content]').forEach((el) => {
      if (el === target) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  #ensureSkeleton() {
    if (document.querySelector('.page-nav-skeleton')) return;

    document.querySelectorAll('[data-main-page-nav-content]').forEach((el) => {
      el.setAttribute('hidden', '');
    });

    const skeleton = document.createElement('div');
    skeleton.className = 'page-nav-skeleton';
    skeleton.setAttribute('aria-busy', 'true');
    skeleton.setAttribute('aria-live', 'polite');
    skeleton.innerHTML = `
      <div class="page-nav-skeleton__bar page-nav-skeleton__bar--lg"></div>
      <div class="page-nav-skeleton__bar"></div>
      <div class="page-nav-skeleton__bar"></div>
      <div class="page-nav-skeleton__bar page-nav-skeleton__bar--lg"></div>
    `;

    const main = document.querySelector('#MainContent');
    if (main) main.before(skeleton);
    else document.body.appendChild(skeleton);
  }

  #removeSkeleton() {
    const skeleton = document.querySelector('.page-nav-skeleton');
    if (skeleton) skeleton.remove();
  }

  /** @param {string} handle */
  #fetchAndCreate(handle) {
    const cached = this.#fetchCache.get(handle);
    if (cached) return cached;

    const promise = (async () => {
      const response = await fetch(`/pages/${handle}`, {
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedMain = doc.querySelector('#MainContent');
      if (!fetchedMain) throw new Error('No #MainContent in fetched page');

      fetchedMain.querySelectorAll('page-nav').forEach((el) => el.remove());

      const newMain = document.createElement('main');
      newMain.id = `page-nav-content-${handle}`;
      newMain.className = 'content-for-layout';
      newMain.setAttribute('role', 'main');
      newMain.setAttribute('data-main-page-nav-content', '');
      newMain.setAttribute('data-page-handle', handle);
      newMain.setAttribute('hidden', '');
      newMain.innerHTML = fetchedMain.innerHTML;

      const original = document.querySelector('#MainContent');
      if (original) original.after(newMain);
      else document.body.appendChild(newMain);

      customElements.upgrade(newMain);
      this.#activateScripts(newMain);

      return newMain;
    })();

    this.#fetchCache.set(handle, promise);
    promise.catch(() => this.#fetchCache.delete(handle));
    return promise;
  }

  /**
   * Re-executes external `<script>` elements within fetched content. Scripts
   * inserted via `innerHTML` are inert, so any section relying on its script
   * (e.g. employee-templates.js) would never initialize. Scripts whose `src`
   * is already present elsewhere in the document are skipped so they are not
   * evaluated twice.
   *
   * @param {HTMLElement} root
   */
  #activateScripts(root) {
    root.querySelectorAll('script[src]').forEach((script) => {
      const src = script.getAttribute('src');
      if (!src) return;

      const alreadyLoaded = Array.from(
        document.querySelectorAll(`script[src="${CSS.escape(src)}"]`)
      ).some((existing) => !root.contains(existing));

      if (alreadyLoaded) {
        script.remove();
        return;
      }

      const fresh = document.createElement('script');
      for (const attr of script.attributes) {
        fresh.setAttribute(attr.name, attr.value);
      }
      script.replaceWith(fresh);
    });
  }
}

if (!customElements.get('page-nav')) {
  customElements.define('page-nav', PageNav);
}
